import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel,
  ListItemText, MenuItem, Paper, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  DELETE_CUSTOM_FIELD, SAVE_CUSTOM_FIELD, UPDATE_CUSTOM_FIELD,
} from '../../api';
import { useCustomFieldDefinitions } from './CustomFieldsSection';

const ROLE_OPTIONS = [
  ['SUPER_ADMIN', 'Super Admin'],
  ['HR_ADMIN', 'HR / Admin'],
  ['EMPLOYEE', 'Employee'],
];
const FIELD_TYPES = [
  ['TEXT', 'Text'], ['NUMBER', 'Number'], ['DATE', 'Date'],
  ['CHOICE', 'Choice'], ['BOOLEAN', 'Yes / No'],
];
const EMPTY_FORM = {
  field_name: '', field_label: '', field_type: 'TEXT', choices: '',
  minimum_value: '', is_required: false, is_sensitive: false, is_active: true,
  read_roles: ['SUPER_ADMIN', 'HR_ADMIN', 'EMPLOYEE'],
  write_roles: ['SUPER_ADMIN', 'HR_ADMIN'],
};

const slugify = (value) => String(value || '').trim().toLowerCase()
  .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50);

const messageFor = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail || data.error) return data.detail || data.error;
  return Object.entries(data).map(([key, value]) => `${key}: ${String(value)}`).join(' ');
};

export default function CustomFieldsSettings() {
  const { definitions, loading, error, reload } = useCustomFieldDefinitions();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const close = () => {
    setOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };
  const add = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };
  const edit = (definition) => {
    setEditingId(definition.id);
    setForm({
      ...definition,
      choices: (definition.choices_json || []).join(', '),
      minimum_value: definition.minimum_value ?? '',
    });
    setOpen(true);
  };
  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const payload = {
      field_name: slugify(form.field_name || form.field_label),
      field_label: form.field_label.trim(),
      field_type: form.field_type,
      choices_json: form.field_type === 'CHOICE'
        ? form.choices.split(',').map((item) => item.trim()).filter(Boolean) : [],
      minimum_value: form.field_type === 'NUMBER' && form.minimum_value !== ''
        ? form.minimum_value : null,
      is_required: form.is_required,
      is_sensitive: form.is_sensitive,
      is_active: form.is_active,
      read_roles: form.read_roles,
      write_roles: form.write_roles,
    };
    try {
      if (editingId) await UPDATE_CUSTOM_FIELD(editingId, payload);
      else await SAVE_CUSTOM_FIELD(payload);
      await reload();
      setMessage({ severity: 'success', text: editingId ? 'Custom field updated.' : 'Custom field created.' });
      close();
    } catch (requestError) {
      setMessage({ severity: 'error', text: messageFor(requestError, 'Unable to save custom field.') });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (definition) => {
    setMessage(null);
    try {
      await DELETE_CUSTOM_FIELD(definition.id);
      await reload();
      setMessage({ severity: 'success', text: `${definition.field_label} was deactivated.` });
    } catch (requestError) {
      setMessage({ severity: 'error', text: messageFor(requestError, 'Unable to deactivate custom field.') });
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Employee Custom Fields</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure approved company-specific fields shown on employee forms and profiles.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={add}>Add field</Button>
      </Box>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
      {loading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress aria-label="Loading custom fields" /></Stack>}
      {!loading && error && <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>{error}</Alert>}
      {!loading && !error && definitions.length === 0 && (
        <Alert severity="info">No custom employee fields have been configured.</Alert>
      )}
      {!loading && !error && definitions.length > 0 && (
        <Stack spacing={1.5}>
          {definitions.map((definition) => (
            <Paper key={definition.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{definition.field_label}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                    <Chip size="small" label={definition.field_type} />
                    {definition.is_required && <Chip size="small" label="Required" color="primary" variant="outlined" />}
                    {definition.is_sensitive && <Chip size="small" label="Sensitive" color="warning" variant="outlined" />}
                    {!definition.is_active && <Chip size="small" label="Inactive" />}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button startIcon={<EditOutlinedIcon />} onClick={() => edit(definition)}>Edit</Button>
                  {definition.is_active && (
                    <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => deactivate(definition)}>
                      Deactivate
                    </Button>
                  )}
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit custom field' : 'Add custom field'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            <TextField
              label="Field label" required value={form.field_label}
              onChange={(event) => {
                setValue('field_label', event.target.value);
                if (!editingId) setValue('field_name', slugify(event.target.value));
              }}
            />
            <TextField
              label="Field key" required value={form.field_name}
              disabled={Boolean(editingId)}
              onChange={(event) => setValue('field_name', slugify(event.target.value))}
              helperText="Stable key used to store employee values."
            />
            <FormControl fullWidth>
              <InputLabel>Field type</InputLabel>
              <Select value={form.field_type} label="Field type" onChange={(event) => setValue('field_type', event.target.value)}>
                {FIELD_TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            {form.field_type === 'CHOICE' && (
              <TextField
                label="Choices" value={form.choices}
                onChange={(event) => setValue('choices', event.target.value)}
                helperText="Enter comma-separated choices."
              />
            )}
            {form.field_type === 'NUMBER' && (
              <TextField
                label="Minimum value (optional)" type="number" inputProps={{ step: 'any' }}
                value={form.minimum_value}
                onChange={(event) => setValue('minimum_value', event.target.value)}
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Roles allowed to view</InputLabel>
              <Select
                multiple value={form.read_roles} label="Roles allowed to view"
                onChange={(event) => setValue('read_roles', event.target.value)}
                renderValue={(selected) => selected.join(', ')}
              >
                {ROLE_OPTIONS.map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={form.read_roles.includes(value)} /><ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Roles allowed to edit</InputLabel>
              <Select
                multiple value={form.write_roles} label="Roles allowed to edit"
                onChange={(event) => setValue('write_roles', event.target.value)}
                renderValue={(selected) => selected.join(', ')}
              >
                {ROLE_OPTIONS.map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={form.write_roles.includes(value)} /><ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={form.is_required} onChange={(event) => setValue('is_required', event.target.checked)} />} label="Required" />
              <FormControlLabel control={<Switch checked={form.is_sensitive} onChange={(event) => setValue('is_sensitive', event.target.checked)} />} label="Sensitive" />
              {editingId && <FormControlLabel control={<Switch checked={form.is_active} onChange={(event) => setValue('is_active', event.target.checked)} />} label="Active" />}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained" onClick={save} disabled={saving || !form.field_label.trim() || !form.field_name}
          >
            {saving ? 'Saving...' : 'Save field'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
